import { Box, Stack, Typography } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { CompanyListTop } from "@/features/company/components/company-list/CompanyListTop";
import { CompanyTable } from "@/features/company/components/company-list/CompanyTable";
import { useCompanyList } from "@/api/services/main/company";
import { usePagination } from "@/hooks/usePagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pagination } from "@/components/elements/Pagination";
import { divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { IconCompanyListImport, IconPlus } from "@/assets/icons";
import { Link } from "react-router-dom";
import { ImportCompanyModal } from "@/features/company/components/company-list/ImportCompanyModal";
import { useGetMainIndustryList, useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { useVisible } from "@/hooks/useVisible";
import {
  COMPANY_DISPLAY_COLUMNS_STORAGE_KEY,
  COMPANY_ADVANCED_FILTERS_STORAGE_KEY,
  COMPANY_APPLIED_CONDITION_NAME_STORAGE_KEY,
  COMPANY_APPLIED_CONDITION_ID_STORAGE_KEY,
  COMPANY_DEFAULT_SEEDED_STORAGE_KEY,
} from "@/features/company/constants/common";
import { useSanitizedSortParams } from "@/hooks/useSanitizedSortParams";
import { useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";
import { COMPANY_SORTABLE_FIELDS } from "@/constants/sortable-fields";
import { AppRoutes } from "@/routes/config";
import { useSearchParams } from "react-router-dom";
import { START_PAGE } from "@/constants/pagination";
import { DEFAULT_PER_PAGE } from "@/constants/pagination";
import {
  TCompanyFilterFormExtended,
  TCompanyDisplayColumnSetting,
  defaultCompanyFilterValues,
  defaultCompanyDisplayColumns,
  normalizeCompanyDateFields,
  normalizeCompanyMultiSelectFields,
  stripEmptyCompanyFilterFields,
  isEmptyCompanyFilterValue,
  COMPANY_LOCKED_COLUMN_IDS,
} from "@/features/company/components/company-list/company-filter-shared";
import { TGetCompanyListReqDto } from "@/api/services/main/company/dtos/get-company-list.dto";
import { useSavedConditionList } from "@/api/services/main/saved-condition";
import { enforceLockedColumns, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import {
  clearSessionFilterState,
  dropLegacyLocalFilterState,
  readSessionFilterState,
  writeSessionFilterState,
} from "@/utils/session-filter-state";
import dayjs from "dayjs";

export const CompanyListPage = () => {
  const pagReq = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();

  useSanitizedPageParam({ basePath: AppRoutes.companyList });
  const { sortValue } = useSanitizedSortParams({
    allowedFields: [...COMPANY_SORTABLE_FIELDS],
  });

  // The applied condition follows the filters: kept across in-app navigation, dropped on
  // reload so the default condition can claim the list again.
  const [appliedConditionName, setAppliedConditionName] = useState<string>(
    () => readSessionFilterState<string>(COMPANY_APPLIED_CONDITION_NAME_STORAGE_KEY) ?? "",
  );
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(
    () => readSessionFilterState<number>(COMPANY_APPLIED_CONDITION_ID_STORAGE_KEY),
  );

  const handleAppliedConditionNameChange = useCallback((name: string) => {
    setAppliedConditionName(name);
    if (name) {
      writeSessionFilterState(COMPANY_APPLIED_CONDITION_NAME_STORAGE_KEY, name);
    } else {
      clearSessionFilterState(COMPANY_APPLIED_CONDITION_NAME_STORAGE_KEY);
    }
  }, []);

  const handleAppliedConditionIdChange = useCallback((id: number | null) => {
    setAppliedConditionId(id);
    if (id !== null) {
      writeSessionFilterState(COMPANY_APPLIED_CONDITION_ID_STORAGE_KEY, id);
    } else {
      clearSessionFilterState(COMPANY_APPLIED_CONDITION_ID_STORAGE_KEY);
    }
  }, []);

  // Kept for the lifetime of the document only — a reload falls back to the default
  // saved condition, or to no filter when the user has none.
  const [advancedFilters, setAdvancedFilters] = useState<TCompanyFilterFormExtended | null>(() => {
    const saved = readSessionFilterState<Partial<TCompanyFilterFormExtended>>(COMPANY_ADVANCED_FILTERS_STORAGE_KEY);
    if (!saved) return null;
    return {
      ...defaultCompanyFilterValues,
      ...normalizeCompanyMultiSelectFields(normalizeCompanyDateFields(saved)),
    } as TCompanyFilterFormExtended;
  });

  // These keys were persisted to localStorage before the filter became session-scoped.
  useEffect(() => {
    dropLegacyLocalFilterState([
      COMPANY_ADVANCED_FILTERS_STORAGE_KEY,
      COMPANY_APPLIED_CONDITION_NAME_STORAGE_KEY,
      COMPANY_APPLIED_CONDITION_ID_STORAGE_KEY,
    ]);
  }, []);

  // On a fresh document the session holds nothing, so the user's default condition seeds
  // the list. Applying it only once keeps a later "clear" from being undone on the next
  // render, and the ref survives the remount that returning from a detail page causes.
  const { data: savedConditionsData } = useSavedConditionList("company");
  const hasSeededDefault = useRef(readSessionFilterState<boolean>(COMPANY_DEFAULT_SEEDED_STORAGE_KEY) ?? false);

  useEffect(() => {
    if (hasSeededDefault.current || !savedConditionsData) return;
    hasSeededDefault.current = true;
    writeSessionFilterState(COMPANY_DEFAULT_SEEDED_STORAGE_KEY, true);

    // getList already orders 自分のみ ahead of チーム全体, so the first default is the one
    // that wins when the user has set both.
    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    const filters = {
      ...defaultCompanyFilterValues,
      ...normalizeCompanyMultiSelectFields(normalizeCompanyDateFields(fallback.filters ?? {})),
    } as TCompanyFilterFormExtended;
    setAdvancedFilters(filters);
    writeSessionFilterState(COMPANY_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyCompanyFilterFields(filters));
    setAppliedConditionName(fallback.title);
    writeSessionFilterState(COMPANY_APPLIED_CONDITION_NAME_STORAGE_KEY, fallback.title);
    setAppliedConditionId(Number(fallback.id));
    writeSessionFilterState(COMPANY_APPLIED_CONDITION_ID_STORAGE_KEY, Number(fallback.id));
    const seededColumns = resolveSavedConditionColumns(fallback, defaultCompanyDisplayColumns);
    setDisplayColumnSettings(seededColumns);
    localStorage.setItem(COMPANY_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(seededColumns));
  }, [savedConditionsData]);

  const resetToFirstPage = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(START_PAGE));
      return next;
    });
  }, [setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TCompanyFilterFormExtended) => {
      const hasAnyFilter = Object.entries(filters).some(([key, v]) => {
        if (key === "isShowCompanyArchive") return v !== 0;
        return !isEmptyCompanyFilterValue(v);
      });
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        writeSessionFilterState(COMPANY_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyCompanyFilterFields(filters));
      } else {
        setAdvancedFilters(null);
        clearSessionFilterState(COMPANY_ADVANCED_FILTERS_STORAGE_KEY);
      }
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  // Display column settings (persisted in localStorage)
  const [displayColumnSettings, setDisplayColumnSettings] = useState<TCompanyDisplayColumnSetting[] | null>(() => {
    const saved = localStorage.getItem(COMPANY_DISPLAY_COLUMNS_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });
  const lockedDisplayColumnSettings = useMemo(
    () => displayColumnSettings && enforceLockedColumns(displayColumnSettings, COMPANY_LOCKED_COLUMN_IDS),
    [displayColumnSettings],
  );

  const handleApplyDisplaySettings = useCallback((columns: TCompanyDisplayColumnSetting[]) => {
    setDisplayColumnSettings(columns);
  }, []);

  // Build API query from advanced filters
  const query: TGetCompanyListReqDto = {
    page: pagReq.page || START_PAGE,
    perPage: pagReq.perPage || DEFAULT_PER_PAGE,
    ...(advancedFilters && {
      ...(advancedFilters.managementNumber ? { managementNumber: advancedFilters.managementNumber } : {}),
      ...(advancedFilters.companyName?.length ? { companyName: advancedFilters.companyName } : {}),
      ...(advancedFilters.companySpecificIndustryId?.length
        ? { companySpecificIndustryId: advancedFilters.companySpecificIndustryId.map(Number) }
        : {}),
      ...(advancedFilters.prefecture?.length ? { prefecture: advancedFilters.prefecture } : {}),
      ...(advancedFilters.city?.length ? { city: advancedFilters.city } : {}),
      ...(advancedFilters.numberOfEmployees ? { numberOfEmployees: advancedFilters.numberOfEmployees } : {}),
      ...(advancedFilters.contactPersonName?.length ? { contactPersonName: advancedFilters.contactPersonName } : {}),
      ...(advancedFilters.jobCount?.length ? { jobCount: advancedFilters.jobCount } : {}),
      ...(advancedFilters.registrationDateFrom ? { registrationDateFrom: dayjs(advancedFilters.registrationDateFrom).format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.registrationDateTo ? { registrationDateTo: dayjs(advancedFilters.registrationDateTo).format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.isShowCompanyArchive ? { isShowCompanyArchive: advancedFilters.isShowCompanyArchive } : {}),
    }),
  };

  if (sortValue) {
    query.sorts = [sortValue as `${string}|${string}`];
  }

  const { data, isLoading } = useCompanyList(query);

  const { mainIndustryOptions } = useGetMainIndustryList();
  const { data: companySpecificIndustryList } = useGetCompanySpecificIndustryList();
  const companyModal = useVisible();

  return (
    <Stack minHeight="100%">
      <Stack mb={3} justifyContent="space-between" alignItems="center" direction="row" width="100%">
        <Typography variant="head20Bold">企業管理</Typography>
        <Box display="flex" gap={2}>
          <Button size="sm" onClick={companyModal.open} variant="outline" startIcon={<IconCompanyListImport width={20} height={20} />}>
            一括インポート
          </Button>
          <Button size="sm" component={Link} to={AppRoutes.companyCreate} startIcon={<IconPlus width={20} height={20} />}>
            新規企業登録
          </Button>
        </Box>
      </Stack>
      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack>
          <CompanyListTop
            pagination={data?.pagination}
            onApplyAdvancedFilters={handleApplyAdvancedFilters}
            onApplyDisplaySettings={handleApplyDisplaySettings}
            appliedConditionName={appliedConditionName}
            appliedConditionId={appliedConditionId}
            onAppliedConditionNameChange={handleAppliedConditionNameChange}
            onAppliedConditionIdChange={handleAppliedConditionIdChange}
            currentDisplayColumns={lockedDisplayColumnSettings}
            currentAdvancedFilters={advancedFilters}
          />
          <Stack spacing={3}>
            <CompanyTable rows={data?.data ?? []} isLoading={isLoading} displayColumnSettings={lockedDisplayColumnSettings} />
            <Pagination totalPage={data?.pagination?.totalPage ?? 1} />
          </Stack>
        </Stack>
      </InnerBox>
      <ImportCompanyModal companyModal={companyModal} listIndustry={mainIndustryOptions} listCompanySpecificIndustry={companySpecificIndustryList} />
    </Stack>
  );
};
